import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon.js';
import Tooltip from '../Tooltip/Tooltip.js';
import { baseClass } from '../Link/Link.js';
import { CommonClasses } from '../../commonClasses.js';

const BUTTON_PROPS = {
  size: ['xlarge', 'large', 'medium', 'small', 'xsmall', 'tiny'],
  type: ['button', 'submit', 'reset'],
  variant: ['default', 'primary', 'secondary', 'danger', 'link', 'success']
};
const LINK_ONLY_SIZES = ['xsmall', 'tiny'];
const getSize = (size, variant) => {
  if (LINK_ONLY_SIZES.includes(size) && variant !== 'link') {
    return 'medium';
  }
  return size;
};
const buttonPropTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  fill: PropTypes.bool,
  iconOnly: PropTypes.bool,
  iconProps: PropTypes.shape({
    fill: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    position: PropTypes.string
  }),
  round: PropTypes.bool,
  size: PropTypes.oneOf(BUTTON_PROPS.size),
  /** shorthand for tooltipProps.content */
  tooltip: PropTypes.node,
  /** props that will be passed to the Tooltip component */
  tooltipProps: PropTypes.shape({
    content: PropTypes.node
  }),
  type: PropTypes.oneOf(BUTTON_PROPS.type),
  variant: PropTypes.oneOf(BUTTON_PROPS.variant)
};
const Button = /*#__PURE__*/forwardRef(({
  active,
  children,
  className,
  fill = false,
  iconOnly = false,
  iconProps,
  round = false,
  size = 'medium',
  tooltip,
  tooltipProps,
  type = 'button',
  variant = 'primary',
  ...otherProps
}, ref) => {
  const baseClass$1 = 'nui-button';
  const labelClass = `${baseClass$1}-label`;
  const hiddenLabelClass = iconOnly && `${labelClass}--hidden`;
  const sizeClass = `${variant === 'link' ? baseClass : baseClass$1}--${getSize(size, variant)}`;
  const styleClass = `${baseClass$1}--${variant}`;
  const iconClass = iconProps && iconProps.name ? `${baseClass$1}--with-icon` : false;
  const iconOnlyClass = iconOnly ? `${iconClass}-only` : false;
  const fillClass = fill && `${baseClass$1}--fill`;
  const activeClass = active && CommonClasses.ACTIVE;
  const linkClass = variant === 'link' && `${baseClass}--default`;
  const roundClass = round && CommonClasses.ROUND;
  const iconPosition = iconProps && iconProps.position || 'left';
  const tooltipContent = tooltip || tooltipProps?.content;
  const buttonIcon = iconProps && iconProps.name && /*#__PURE__*/React__default.createElement(Icon, _extends({
    className: `${baseClass$1}-icon--${iconPosition}`
  }, iconProps, {
    name: iconProps.name
  }));
  const button = /*#__PURE__*/React__default.createElement("button", _extends({
    className: cx(baseClass$1, sizeClass, styleClass, iconClass, iconOnlyClass, fillClass, linkClass, activeClass, roundClass, className),
    type: type,
    ref: ref
  }, otherProps), iconPosition === 'left' && buttonIcon, /*#__PURE__*/React__default.createElement("span", {
    className: cx(labelClass, hiddenLabelClass)
  }, children), iconPosition !== 'left' && buttonIcon);
  if (!tooltipContent) return button;
  return /*#__PURE__*/React__default.createElement(Tooltip, _extends({}, tooltipProps, {
    content: tooltipContent
  }), button);
});
Button.propTypes = buttonPropTypes;
Button.displayName = 'NuiButton';
var Button$1 = Button;

export { BUTTON_PROPS, Button$1 as default };
